import { Component } from '@angular/core';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { Tpl3plService } from '../../../services/tpl3pl.service';

interface VoidAuthItem {
	auth_id: string;
	transid: string;
	orderno: string;
	debtorno: string;
	customer_name: string;
	auth_amount: number;
	transdate: string;
	auth_status: string;
	// post-validation
	stripe_status?: string;
	voidable?: boolean;
	validated?: boolean;
	validate_note?: string;
	// post-void
	voiding?: boolean;
	voided?: boolean;
	void_error?: string;
}

@Component({
	selector: 'app-tpl3pl-void-auths',
	templateUrl: './tpl3pl-void-auths.component.html',
	styleUrls: ['./tpl3pl-void-auths.component.scss']
})
export class Tpl3plVoidAuthsComponent {
	orderInput = '';
	dateFrom: Date | null = null;
	dateTo: Date | null = null;
	items: VoidAuthItem[] = [];
	loading = false;
	loaded = false;
	loadSource: 'manual' | 'backorder' | null = null;
	validating = false;
	validated = false;

	readonly BACKORDER_STATUS_ID = 51;

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {}

	private parseOrderNos(): string[] {
		return [...new Set(
			this.orderInput.split(/[\s,;]+/).map(s => s.trim()).filter(s => s.length > 0)
		)];
	}

	get parsedCount(): number { return this.parseOrderNos().length; }
	get pendingCount(): number { return this.items.filter(i => !i.voided).length; }
	get voidedCount(): number { return this.items.filter(i => i.voided).length; }

	get voidableCount(): number {
		return this.validated
			? this.items.filter(i => i.voidable && !i.voided).length
			: this.pendingCount;
	}

	get alreadyCancelledCount(): number {
		return this.items.filter(i => i.validated && i.stripe_status === 'canceled').length;
	}

	get alreadyCapturedCount(): number {
		return this.items.filter(i => i.validated && i.stripe_status === 'succeeded').length;
	}

	get validateErrorCount(): number {
		return this.items.filter(i => i.validated && i.stripe_status === 'error').length;
	}

	get pendingTotal(): number {
		const src = this.validated
			? this.items.filter(i => i.voidable && !i.voided)
			: this.items.filter(i => !i.voided);
		return src.reduce((s, i) => s + i.auth_amount, 0);
	}

	private applyItems(raw: any[]): void {
		this.items = raw.map((item: any) => ({
			...item,
			stripe_status: undefined,
			voidable: undefined,
			validated: false,
			validate_note: '',
			voiding: false,
			voided: false,
			void_error: ''
		}));
		this.validated = false;
	}

	private fmtDate(d: Date | null): string {
		if (!d) { return ''; }
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private datePayload(): { date_from?: string; date_to?: string } {
		const p: { date_from?: string; date_to?: string } = {};
		const from = this.fmtDate(this.dateFrom);
		const to   = this.fmtDate(this.dateTo);
		if (from) { p.date_from = from; }
		if (to)   { p.date_to   = to;   }
		return p;
	}

	loadBackorderAuths(): void {
		this.loading = true;
		this.loaded = false;
		this.items = [];
		this.loadSource = 'backorder';
		this.tpl3plService.listBackorderAuths({ orderstatusid: this.BACKORDER_STATUS_ID, ...this.datePayload() }).subscribe({
			next: (res) => {
				this.loading = false;
				this.loaded = true;
				this.applyItems(res.items || []);
				if (this.items.length === 0) {
					this.globalSearchService.showNotification('No open authorizations found', 'info', 'bottom', 'right');
				}
			},
			error: () => {
				this.loading = false;
				this.globalSearchService.showNotification('Error loading authorizations', 'danger', 'bottom', 'right');
			}
		});
	}

	loadAuths(): void {
		const nos = this.parseOrderNos();
		if (nos.length === 0) {
			this.globalSearchService.showNotification('Enter at least one order number', 'warning', 'bottom', 'right');
			return;
		}
		this.loading = true;
		this.loaded = false;
		this.items = [];
		this.loadSource = 'manual';
		this.tpl3plService.listAuthsByOrderList({ order_list: nos, ...this.datePayload() }).subscribe({
			next: (res) => {
				this.loading = false;
				this.loaded = true;
				this.applyItems(res.items || []);
				if (this.items.length === 0) {
					this.globalSearchService.showNotification('No open authorizations found for those orders', 'info', 'bottom', 'right');
				}
			},
			error: () => {
				this.loading = false;
				this.globalSearchService.showNotification('Error loading authorizations', 'danger', 'bottom', 'right');
			}
		});
	}

	validateAll(): void {
		if (this.items.length === 0) { return; }
		this.validating = true;
		const payload = this.items.map(i => ({ auth_id: i.auth_id, transid: i.transid }));
		this.tpl3plService.validateAuthStatuses(payload).subscribe({
			next: (res) => {
				this.validating = false;
				this.validated = true;
				const byTransid: Record<string, any> = {};
				for (const r of (res.results || [])) {
					byTransid[r.transid] = r;
				}
				for (const item of this.items) {
					const r = byTransid[item.transid];
					if (r) {
						item.stripe_status = r.stripe_status;
						item.voidable = !!r.voidable;
						item.validate_note = r.note || '';
						item.validated = true;
					}
				}
				const cancelledCount = this.alreadyCancelledCount;
				const capturedCount = this.alreadyCapturedCount;
				const voidable = this.voidableCount;
				this.globalSearchService.showNotification(
					`Validated: ${voidable} voidable, ${cancelledCount} already cancelled, ${capturedCount} already captured`,
					cancelledCount > 0 || capturedCount > 0 ? 'warning' : 'success',
					'bottom', 'right'
				);
			},
			error: () => {
				this.validating = false;
				this.globalSearchService.showNotification('Stripe validation failed', 'danger', 'bottom', 'right');
			}
		});
	}

	void(item: VoidAuthItem): void {
		if (item.voiding || item.voided) { return; }
		if (this.validated && !item.voidable) { return; }
		item.voiding = true;
		item.void_error = '';
		this.tpl3plService.voidAuth({ auth_id: item.auth_id, transid: item.transid }).subscribe({
			next: (res) => {
				item.voiding = false;
				if (res.success) {
					item.voided = true;
					this.globalSearchService.showNotification(
						`Voided auth for order ${item.orderno}`, 'success', 'bottom', 'right'
					);
				} else {
					item.void_error = res.message || 'Void failed';
					this.globalSearchService.showNotification(item.void_error!, 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				item.voiding = false;
				item.void_error = err?.error?.message || 'Void failed';
				this.globalSearchService.showNotification(item.void_error!, 'danger', 'bottom', 'right');
			}
		});
	}

	voidAll(): void {
		const pending = this.validated
			? this.items.filter(i => i.voidable && !i.voided && !i.voiding)
			: this.items.filter(i => !i.voided && !i.voiding);
		if (pending.length === 0) {
			this.globalSearchService.showNotification('Nothing to void', 'warning', 'bottom', 'right');
			return;
		}
		pending.forEach(item => this.void(item));
	}

	rowClass(item: VoidAuthItem): string {
		if (item.voided) { return 'va-row-voided'; }
		if (item.void_error) { return 'va-row-error'; }
		if (item.validated && item.stripe_status === 'canceled') { return 'va-row-cancelled'; }
		if (item.validated && item.stripe_status === 'succeeded') { return 'va-row-captured'; }
		if (item.validated && item.stripe_status === 'error') { return 'va-row-validate-error'; }
		return '';
	}

	stripeStatusLabel(item: VoidAuthItem): string {
		switch (item.stripe_status) {
			case 'requires_capture': return 'Open';
			case 'canceled':         return 'Cancelled on Stripe';
			case 'succeeded':        return 'Already captured';
			case 'error':            return 'Lookup failed';
			default:                 return item.stripe_status || '';
		}
	}

	stripeStatusClass(item: VoidAuthItem): string {
		switch (item.stripe_status) {
			case 'requires_capture': return 'va-stripe-open';
			case 'canceled':         return 'va-stripe-cancelled';
			case 'succeeded':        return 'va-stripe-captured';
			case 'error':            return 'va-stripe-error';
			default:                 return '';
		}
	}

	fmt(v: number | null | undefined): string {
		if (v == null) { return '—'; }
		return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	}
}
